import { Badge, Button, Flex, HStack, IconButton, Input, InputGroup, InputLeftElement, TableContainer, Tooltip, useToast } from "@chakra-ui/react";
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { SectionHeader } from "../../../components";
import { LuSearch } from "react-icons/lu";
import { IconDownload, IconEdit, IconFileSpreadsheet, IconFileTypeDoc, IconFileTypeDocx, IconHourglassHigh, IconPlus } from "@tabler/icons-react";
import { DataTable } from "am-tax-fe-core";
import { BenchmarkInput, downloadBenchmarkOutputAsync, generateBenchmarkAsync, downloadBenchmarkTemplateAsync } from "../../../api/benchmark/benchmarkApi";
import { TableRowProps } from "../../../utils/types";
import { useGetBenchmarkInputsByClientId } from "../../../api/benchmark/benchmarkQueries";
import { createColumnHelper } from "@tanstack/react-table";
import { BenchmarkModal } from "./BenchmarkModal";
import { FaGears } from "react-icons/fa6";

interface BenchmarkPanelProps {
    clientId?: string;
}

const BenchmarkActionsCell: FunctionComponent<{
    benchmark: BenchmarkInput;
    generatingBenchmarkId: string | null;
    onGenerate: (benchmark: BenchmarkInput) => void;
    onEdit: (benchmark: BenchmarkInput) => void;
    onDownload: (benchmark: BenchmarkInput) => void;
}> = ({ benchmark, generatingBenchmarkId, onGenerate, onEdit, onDownload }) => {
    const generatedBenchmark = benchmark.benchmarkOutput;
    const isGenerating = generatingBenchmarkId === benchmark.id;
    const isCompleted = generatedBenchmark?.status === "Completed";

    return (
        <HStack spacing={2}>
            <Tooltip shouldWrapChildren label="Edit Benchmark">
                <IconButton
                    icon={<IconEdit />}
                    aria-label="Edit Benchmark"
                    variant="outline"
                    size="sm"
                    onClick={evt => {
                        evt.stopPropagation();
                        onEdit(benchmark);
                    }}
                />
            </Tooltip>

            {/* Generate Benchmark Button: Disabled if no transactions, with explanatory tooltip */}
            <Tooltip shouldWrapChildren label="Generate Benchmark">
                <IconButton
                    icon={<FaGears />}
                    aria-label="Generate Benchmark"
                    variant="outline"
                    size="sm"
                    colorScheme={"blue"}
                    isLoading={isGenerating}
                    isDisabled={generatedBenchmark?.status === "Processing" || generatedBenchmark?.status === "Queued"}
                    onClick={evt => {
                        evt.stopPropagation();
                        onGenerate(benchmark);
                    }}
                />
            </Tooltip>

            {isCompleted && (
                <Tooltip shouldWrapChildren label="Download Report">
                    <IconButton
                        icon={<IconDownload />}
                        aria-label="Download Report"
                        variant="outline"
                        size="sm"
                        colorScheme="green"
                        onClick={evt => {
                            evt.stopPropagation();
                            onDownload(benchmark);
                        }}
                    />
                </Tooltip>
            )}
        </HStack>
    );
};

export const BenchmarkPanel: FunctionComponent<BenchmarkPanelProps> = ({ clientId }) => {
    const [generatingBenchmarkId, setGeneratingBenchmarkId] = useState<string | null>(null);
    const toast = useToast();
    const { data: benchmarkInputs = [], isLoading, refetch } = useGetBenchmarkInputsByClientId(clientId);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [benchmarkData, setBenchmarkData] = useState<BenchmarkInput | undefined>(undefined);
    const [showCreateOrEditBenchmarkDialog, setShowCreateOrEditBenchmarkDialog] = useState<boolean>(false);
    const columnHelper = createColumnHelper<BenchmarkInput>();

    // Polling for benchmark status updates
    useEffect(() => {
        // Check if any report is in Processing state
        const hasActiveBenchmark = benchmarkInputs.some(
            (r: BenchmarkInput) => r.benchmarkOutput?.status === "Processing" || r.benchmarkOutput?.status === "Queued",
        );
        if (!hasActiveBenchmark) return;
        const interval = setInterval(() => {
            refetch();
        }, 5000); // Poll every 5 seconds
        return () => clearInterval(interval);
    }, [benchmarkInputs, refetch]);

    const filteredData = useMemo(() => {
        if (searchTerm.trim() === "") {
            return benchmarkInputs;
        } else {
            const value = searchTerm.trim().toLowerCase();
            return benchmarkInputs.filter(
                (b: BenchmarkInput) =>
                    b.blobStorageLink?.toLowerCase().includes(value) ||
                    b.region?.toLowerCase().includes(value) ||
                    b.serviceType?.toLowerCase().includes(value) ||
                    b.minimumAverageTotalRevenue?.toLowerCase().includes(value) ||
                    b.minimumInventory?.toLowerCase().includes(value) ||
                    b.minimumPPE?.toLowerCase().includes(value) ||
                    b.inventorySalesRatio?.toLowerCase().includes(value),
            );
        }
    }, [benchmarkInputs, searchTerm]);

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSearchTerm(value);
    }

    const enableEditBenchmarkModal = (benchmark: BenchmarkInput) => {
        setBenchmarkData(benchmark);
        setShowCreateOrEditBenchmarkDialog(true);
    };

    const closeEditBenchmarkModal = () => {
        setShowCreateOrEditBenchmarkDialog(false);
        setBenchmarkData(undefined);
        refetch();
    };

    async function downloadBenchmarkTemplate() {
        try {
            toast({
                title: "Downloading Example Template",
                description: "Fetching benchmark analysis Excel template...",
                status: "info",
                duration: 2000,
                isClosable: true,
            });

            const response = await downloadBenchmarkTemplateAsync();

            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || "Benchmark_Input_Template.xlsx";

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Benchmark Input Template Downloaded",
                description: "The benchmark input template has been downloaded.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading input template:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download input template",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    async function downloadBenchmark(benchmark: BenchmarkInput) {
        try {
            if (!benchmark.benchmarkOutput || !benchmark.benchmarkOutput.id) {
                throw new Error("No generated benchmark output available for download.");
            }

            const response = await downloadBenchmarkOutputAsync(benchmark.benchmarkOutput.id);

            // Get the filename from the response headers or use a default
            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || `Transfer_Pricing_Report_${benchmark.id}.xlsx`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Download Complete",
                description: `Benchmark output for ${benchmark.region} has been downloaded`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading benchmark output:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download benchmark output",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    async function generateBenchmark(benchmark: BenchmarkInput) {
        const benchmarkId = benchmark.id;

        if (!benchmarkId) {
            return;
        }

        setGeneratingBenchmarkId(benchmarkId);

        try {
            toast({
                title: "Generating Benchmark",
                description: `Starting generation for ${benchmark.region}`,
                status: "info",
                duration: 3000,
                isClosable: true,
            });

            await generateBenchmarkAsync(benchmarkId);

            await refetch();
        } catch (error) {
            console.error("Error generating benchmark:", error);
            toast({
                title: "Generation Failed",
                description: error instanceof Error ? error.message : "Failed to generate benchmark",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setGeneratingBenchmarkId(null);
        }
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Benchmark Inputs">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>
                    <Input placeholder="Search Benchmark Inputs" onChange={handleFilter} />
                </InputGroup>
                <Button leftIcon={<IconFileSpreadsheet />} variant="outline" flexShrink="0" onClick={downloadBenchmarkTemplate}>
                    Benchmark Template
                </Button>
                <Button leftIcon={<IconPlus />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditBenchmarkDialog(true)}>
                    Add Benchmark Input
                </Button>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                {" "}
                <DataTable
                    enableFilters={false}
                    tableProps={{
                        size: "sm",
                        sx: {
                            "tbody tr": {
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                _hover: {
                                    backgroundColor: "blue.50",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    _dark: {
                                        backgroundColor: "blue.900",
                                    },
                                },
                            },
                        },
                    }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No benchmark inputs found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("region", {
                            header: "Region",
                            cell: (props: TableRowProps<BenchmarkInput>) => props.getValue(),
                        }),
                        columnHelper.accessor("serviceType", {
                            header: "Service Type",
                            cell: (props: TableRowProps<BenchmarkInput>) => props.getValue(),
                        }),
                        columnHelper.accessor("blobStorageLink", {
                            header: "Input File",
                            cell: props => {
                                const blobLink = props.getValue();
                                return blobLink && blobLink !== "/" ? blobLink.split("/").pop() : "No file";
                            },
                        }),
                        columnHelper.accessor(row => row.benchmarkOutput?.status, {
                            id: "status",
                            header: "Status",
                            cell: props => {
                                const status = props.getValue() as string;
                                const generatedBenchmark = props.row.original.benchmarkOutput;

                                // Determine the primary status to display based on generated report status
                                let displayStatus: string;
                                let colorScheme: string;
                                let icon = null;

                                if (generatedBenchmark) {
                                    // Use the actual generated benchmark status from the API
                                    switch (generatedBenchmark.status) {
                                        case "Processing":
                                            displayStatus = "Generating...";
                                            colorScheme = "yellow";
                                            icon = <IconFileTypeDocx size={14} />;
                                            break;
                                        case "Queued":
                                            displayStatus = "Generation Requested";
                                            colorScheme = "orange";
                                            icon = <IconHourglassHigh size={14} />;
                                            break;
                                        case "Completed":
                                            displayStatus = "Ready for Download";
                                            colorScheme = "green";
                                            icon = <IconFileTypeDocx size={14} />;
                                            break;
                                        case "Failed":
                                            displayStatus = "Generation Failed";
                                            colorScheme = "red";
                                            icon = <IconFileTypeDoc size={14} />;
                                            break;
                                        default:
                                            // Fallback to workflow status
                                            displayStatus = "New";
                                            colorScheme = status === "New" ? "blue" : "gray";
                                    }
                                } else {
                                    displayStatus = "New";
                                    colorScheme = "blue";
                                }

                                return (
                                    <Badge colorScheme={colorScheme} variant="subtle" size="sm">
                                        <HStack spacing={1}>
                                            {icon}
                                            <span>{displayStatus}</span>
                                        </HStack>
                                    </Badge>
                                );
                            },
                        }),
                        {
                            id: "Actions",
                            header: "Actions",
                            cell: props => {
                                const row = props.row.original;
                                return (
                                    <BenchmarkActionsCell
                                        benchmark={row}
                                        generatingBenchmarkId={generatingBenchmarkId}
                                        onGenerate={generateBenchmark}
                                        onEdit={enableEditBenchmarkModal}
                                        onDownload={downloadBenchmark}
                                    />
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <BenchmarkModal show={showCreateOrEditBenchmarkDialog} onClose={closeEditBenchmarkModal} rowData={benchmarkData} clientId={clientId} />
        </Flex>
    );
};
